import { MessageDescriptor, useIntl } from 'react-intl'
import { isValidName, useConfig } from 'msp-integrations'
import { useCaptcha } from '@molecules/Captcha/Captcha.hook'
import { FieldsConfiguration } from '@molecules/Field/Field.types'

const getContactReasonOptions = (
  reasons: { id: string; value: MessageDescriptor }[]
): { id: string; value: string; disabled?: string }[] => {
  const { formatMessage } = useIntl()
  return [
    {
      id: '',
      value: formatMessage({
        defaultMessage: '- Reason for contacting us -',
        id: 'msp.contact_form.label.reason'
      }),
      disabled: 'disabled'
    },
    ...reasons.map((r) => ({ id: r.id, value: formatMessage(r.value) }))
  ]
}

const getContactFields = (): FieldsConfiguration => {
  const { formatMessage } = useIntl()
  const {
    theme: { mspComponents }
  } = useConfig()
  const { validate } = useCaptcha()

  const reasonOptions = getContactReasonOptions(
    mspComponents?.contactForm?.reasons || []
  )

  return {
    name: {
      id: 'name',
      type: 'text',
      name: 'name',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Contact Name',
        id: 'msp.contact_form.label.name'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter contact name.',
          id: 'msp.contact_form.error.name'
        }),
        validate: (value) =>
          isValidName(value) ||
          formatMessage({
            defaultMessage: 'Field contains invalid characters',
            id: 'msp.global.form.error.valid_name'
          })
      }
    },
    email: {
      id: 'email',
      type: 'email',
      name: 'email',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Email address',
        id: 'msp.contact_form.label.email'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your email address.',
          id: 'msp.contact_form.error.required_email'
        }),
        pattern: {
          value: new RegExp(
            '^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$',
            'i'
          ),
          message: formatMessage({
            defaultMessage: 'Please enter valid email address.',
            id: 'msp.contact_form.error.valid_email'
          })
        }
      }
    },
    phone: {
      id: 'phone',
      type: 'tel',
      name: 'phone',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Telephone (optional)',
        id: 'msp.contact_form.label.phone'
      })
    },
    Contact_Reason: {
      id: mspComponents?.contactForm?.reasonFieldName || 'Contact_Reason',
      type: 'select',
      name: mspComponents?.contactForm?.reasonFieldName || 'Contact_Reason',
      defaultValues: reasonOptions[0].id,
      rules: {
        required: formatMessage({
          defaultMessage: 'Please select a reason.',
          id: 'msp.contact_form.error.reason'
        })
      },
      options: reasonOptions
    },
    captcha: {
      id: 'captcha',
      type: 'captcha',
      name: 'captcha',
      label: formatMessage({
        defaultMessage: 'Captcha',
        id: 'msp.contact_form.captcha.label'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter the shown character.',
          id: 'msp.contact_form.captcha.error.required'
        }),
        validate: (value) =>
          validate(value) ||
          formatMessage({
            defaultMessage:
              'The captcha you entered is incorrect. Please try again.',
            id: 'msp.contact_form.captcha.error.wrong'
          })
      }
    },
    description: {
      id: 'description',
      type: 'textarea',
      name: 'description',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Description',
        id: 'msp.contact_form.label.description'
      }),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your message.',
          id: 'msp.contact_form.error.description'
        })
      },
      rows: 4,
      cols: 200,
      resizeable: true
    }
  }
}

export default getContactFields
